/**
 * Cloud Files TypeScript Types
 *
 * Types for cloud storage integration including file browsing,
 * metadata, and upload operations.
 */

// =============================================================================
// Cloud Permissions
// =============================================================================

/**
 * Cloud permission types required for cloud file operations.
 * These map to backend RBAC permissions.
 */
export const CLOUD_PERMISSIONS = {
  /** Required to browse cloud connections and list files */
  VIEW: "cloud:view",
  /** Required to initiate cloud file uploads */
  USE: "cloud:use",
} as const;

/**
 * User-friendly messages for cloud permission errors.
 */
export const CLOUD_PERMISSION_ERROR_MESSAGES = {
  VIEW_DENIED:
    "You don't have permission to browse cloud files. Contact your administrator.",
  USE_DENIED:
    "You don't have permission to import from cloud storage. Contact your administrator.",
  GENERIC:
    "Access denied. Contact your administrator for cloud access permissions.",
} as const;

// =============================================================================
// Connection Types
// =============================================================================

/**
 * Cloud provider type string.
 *
 * Note: This type represents provider identifiers from the backend.
 * The backend dynamically registers providers via CloudProviderRegistry.
 * Use `useCloudProviders()` hook to fetch the current list from `/document-sources/providers`.
 *
 * Known providers include "sharepoint", "filenet", "cmis" but more may be added
 * without frontend code changes.
 *
 * Note: 'database' is NOT a cloud provider - it's for ETL jobs only.
 */
export type CloudProviderType = string;

/**
 * All connection types including database and etl-step credentials.
 *
 * 'etl' holds a credential an ETL job step needs (an API key, a bearer token,
 * a bundle of auth fields) rather than something to connect to. The step names
 * it and the backend injects it as ETL_CONN_* environment variables.
 */
export type ConnectionType = "database" | "etl" | CloudProviderType;

/**
 * The connection types that are NOT cloud providers, mirroring the non-registry
 * half of the backend's `get_allowed_connection_types()`. Every type picker
 * renders this list plus `useCloudProviders()` — add a type here once, not per
 * dropdown.
 */
export const NON_CLOUD_CONNECTION_TYPES: {
  value: ConnectionType;
  label: string;
  createLabel: string;
  description: string;
}[] = [
  {
    value: "database",
    label: "Database",
    createLabel: "Database (ETL Jobs)",
    description: "SQL Server, Oracle connections for ETL jobs",
  },
  {
    value: "etl",
    label: "ETL Step Credential",
    createLabel: "ETL Step Credential",
    description:
      "An API key, token, or set of auth fields an ETL job step needs. " +
      "The step names the connection and receives it as ETL_CONN_* environment " +
      "variables, so no credential is stored in the job definition.",
  },
];

/**
 * The human label for a connection type: the shared non-cloud list first, then
 * the runtime provider registry, falling back to the raw identifier.
 *
 * One lookup so a new type cannot render as a bare slug in one place while
 * reading correctly in another — which is exactly how `etl` shipped as a
 * lowercase "etl" chip in the connections table.
 */
export const connectionTypeLabel = (
  connectionType: ConnectionType,
  providers?: { name: string; display_name: string }[],
): string => {
  const nonCloud = NON_CLOUD_CONNECTION_TYPES.find(
    (type) => type.value === connectionType,
  );
  if (nonCloud) return nonCloud.label;
  return (
    providers?.find((provider) => provider.name === connectionType)
      ?.display_name || connectionType
  );
};

// =============================================================================
// Cloud File Metadata
// =============================================================================

/**
 * Metadata for a file or folder in cloud storage.
 */
export interface CloudFileMetadata {
  /** Full path in cloud storage (e.g., '/Documents/report.pdf') */
  path: string;
  /** File or folder name */
  name: string;
  /** File size in bytes (0 for directories) */
  size: number;
  /** Last modified timestamp (ISO 8601) */
  modified_date: string;
  /** MIME type or 'application/directory' for folders */
  content_type: string;
  /** True if this is a directory */
  is_directory: boolean;
  /** Alias for is_directory (for upload requests) */
  is_folder?: boolean;
  /** Provider type (for tracking source in UI) */
  provider_type?: CloudProviderType;
  /** Connection ID (for tracking source in UI) */
  connection_id?: number;
}

/**
 * Recursive tree node for directory structure.
 */
export interface CloudTreeNode {
  /** Metadata for this file/folder */
  metadata: CloudFileMetadata;
  /** Child files/folders (null if not expanded or is a file) */
  children?: CloudTreeNode[] | null;
  /** Parent node (undefined for root nodes) */
  parent?: CloudTreeNode;
  /** Whether children were truncated due to max depth limit */
  truncated?: boolean;
}

// =============================================================================
// Cloud Provider Information
// =============================================================================

/**
 * Information about a registered cloud provider.
 * Fetched from GET /document-sources/providers endpoint.
 */
export interface CloudProviderInfo {
  /** Provider identifier (e.g., 'sharepoint', 'filenet') */
  name: string;
  /** Human-readable provider name (e.g., 'Microsoft SharePoint') */
  display_name: string;
  /** Brief help text describing the provider */
  description: string;
}

// =============================================================================
// Credential Verification
// =============================================================================

/**
 * Response from credential verification.
 */
export interface CredentialVerificationResult {
  /** Whether verification was successful */
  success: boolean;
  /** Success or error message */
  message: string;
}

// =============================================================================
// Cloud Upload Path Item
// =============================================================================

/**
 * A single file or folder path for batch upload.
 */
export interface CloudPathItem {
  /** Full path to the file or folder in cloud storage */
  path: string;
  /** Whether this path is a folder */
  is_folder: boolean;
  /** Maximum depth to traverse for this folder (1-10, default 1). Only applies to folders. */
  max_folder_depth?: number;
  /** Optional list of file extensions to include for this folder upload. Only applies to folders. */
  file_extensions?: string[];
}

/**
 * Error codes for cloud upload operations.
 */
export type CloudUploadErrorCode =
  | "DATASET_NOT_FOUND"
  | "PROVIDER_NOT_FOUND"
  | "CONNECTION_NOT_FOUND"
  | "UNSUPPORTED_FILE_TYPE"
  | "FILE_NOT_FOUND"
  | "PERMISSION_DENIED"
  | "CREDENTIAL_ERROR"
  | "DUPLICATE_FILE"
  | "RATE_LIMIT_EXCEEDED"
  | "INTERNAL_ERROR";

/**
 * Map of error codes to user-friendly messages.
 */
export const CLOUD_UPLOAD_ERROR_MESSAGES: Record<CloudUploadErrorCode, string> =
  {
    DATASET_NOT_FOUND: "The target dataset no longer exists",
    PROVIDER_NOT_FOUND: "Cloud provider is not configured",
    CONNECTION_NOT_FOUND: "Connection was deleted or is inaccessible",
    UNSUPPORTED_FILE_TYPE: "This file type cannot be imported",
    FILE_NOT_FOUND: "File no longer exists in cloud storage",
    PERMISSION_DENIED: "No permission to access this file",
    CREDENTIAL_ERROR: "Connection credentials are invalid",
    DUPLICATE_FILE: "A file with this name already exists",
    RATE_LIMIT_EXCEEDED: "Too many uploads, please try again later",
    INTERNAL_ERROR: "An unexpected error occurred",
  };

// =============================================================================
// Credential Format Types (for form building)
// =============================================================================

/**
 * SharePoint credentials structure.
 */
export interface SharePointCredentials {
  site_url: string;
  tenant_id: string;
  client_id: string;
  /** Certificate-based auth — the only supported method */
  cert_thumbprint: string;
  cert_private_key: string;
}

/**
 * CMIS/FileNet credentials structure.
 */
export interface CMISCredentials {
  cmis_url: string;
  username: string;
  password: string;
}

/**
 * Union of all credential types.
 */
export type CloudCredentials = SharePointCredentials | CMISCredentials;

// =============================================================================
// UI State Types
// =============================================================================

/**
 * Selection state for the cloud file tree.
 */
export interface CloudFileSelection {
  /** Selected files (keyed by path) */
  files: Map<string, CloudFileMetadata>;
  /** Selected folders (keyed by path) */
  folders: Map<string, CloudFileMetadata>;
}

/**
 * Navigation state for cloud file browser.
 */
export interface CloudBrowserState {
  /** Currently selected provider */
  provider: CloudProviderType | null;
  /** Currently selected connection ID */
  connectionId: number | null;
  /** Current directory path */
  currentPath: string;
  /** Set of expanded node paths */
  expandedNodes: Set<string>;
  /** Current selection */
  selection: CloudFileSelection;
}

// =============================================================================
// Batch Upload API Types (Phase 5)
// =============================================================================

/**
 * Status of a cloud upload batch.
 * - discovering: Folder discovery in progress, uploads may have started
 * - uploading: Discovery complete, uploads in progress
 * - completed: All uploads succeeded
 * - completed_with_errors: Some uploads failed
 * - failed: Critical failure (e.g., connection error)
 * - cancelled: Batch was cancelled by user
 */
export type CloudBatchStatus =
  | "discovering"
  | "uploading"
  | "completed"
  | "completed_with_errors"
  | "failed"
  | "cancelled";

/**
 * Discovery status for a batch item.
 */
export type CloudItemDiscoveryStatus =
  | "pending"
  | "scanning"
  | "completed"
  | "failed";

/**
 * Upload status for a batch item.
 */
export type CloudItemUploadStatus =
  | "pending"
  | "queued"
  | "uploading"
  | "completed"
  | "failed";

/**
 * Request to start a batch upload from cloud storage.
 * POST /datasets/{id}/upload-from-cloud
 */
export interface CloudBatchUploadRequest {
  /** Cloud provider type */
  provider_type: CloudProviderType;
  /** ID of external connection with stored credentials */
  external_connection_id: number;
  /** List of files and/or folders to upload */
  paths: CloudPathItem[];
  /** Maximum depth to traverse for folders (1-10, default 1) */
  max_folder_depth?: number;
  /** Optional list of file extensions to include for folder uploads */
  file_extensions?: string[];
  /** Priority for the upload tasks (1-9, default 4) */
  task_priority?: number;
  /** Whether to trigger document processing after upload (default: true) */
  trigger_doc_workflow?: boolean;
}

/**
 * Response from starting a batch upload.
 */
export interface CloudBatchUploadResponse {
  /** Unique batch ID for polling */
  batch_id: string;
  /** Initial status (usually "discovering") */
  status: CloudBatchStatus;
  /** Number of sources (files + folders) submitted */
  discovery_sources_total: number;
  /** Number of sources with completed discovery */
  discovery_sources_completed: number;
  /** Number of files discovered so far */
  files_discovered: number;
}

/**
 * Discovery progress within a batch.
 */
export interface CloudBatchDiscoveryProgress {
  /** Number of sources (files + folders) submitted */
  sources_total: number;
  /** Number of sources with completed discovery */
  sources_completed: number;
  /** Number of files discovered so far */
  files_found: number;
}

/**
 * Upload progress within a batch.
 */
export interface CloudBatchUploadProgress {
  /** Files waiting to start */
  pending: number;
  /** Files currently uploading */
  uploading: number;
  /** Files successfully uploaded */
  completed: number;
  /** Files that failed to upload */
  failed: number;
}

/**
 * Processing status for a source data record (embedded in batch item status).
 * Returned when include_items=true on batch status endpoint.
 */
export interface SDRProcessingStatus {
  /** Raw processing status */
  status: string;
  /** User-friendly display status (e.g., "Reading document", "Indexing", "Ready") */
  display_status: string;
  /** Whether the file is still being processed */
  is_processing: boolean;
  /** Progress percentage (0-100) */
  progress_percentage: number;
  /** Error message if processing failed */
  error_message?: string | null;
  /** Number of chunks created */
  num_chunks: number;
  /** Number of chunks ready for search */
  num_chunks_ready: number;
  /** When the file was uploaded */
  uploaded_at?: string | null;
  /** When the status was last updated */
  last_updated?: string | null;
}

/**
 * Full processing status response for /datasets/processing-status endpoint.
 * Extends SDRProcessingStatus with additional identification fields.
 */
export interface SDRProcessingStatusResponse extends SDRProcessingStatus {
  /** Source data record ID */
  source_data_record_id: number;
  /** Dataset ID (null if SDR not found) */
  dataset_id: number | null;
  /** Filename */
  filename: string;
  /** Alias for num_chunks_ready (backward compatibility) */
  num_chunks_embedded: number;
}

/**
 * Status of an individual item in a batch.
 */
export interface CloudBatchItemStatus {
  /** Item ID */
  id: number;
  /** Type of item */
  item_type: "file" | "folder";
  /** Cloud path */
  cloud_path: string;
  /** Filename (for files) */
  filename?: string;
  /** Discovery status (for folders) */
  discovery_status: CloudItemDiscoveryStatus;
  /** Number of files discovered (for folders) */
  files_discovered: number;
  /** Upload status (for files) */
  upload_status?: CloudItemUploadStatus;
  /** Celery task ID (for tracking) */
  celery_task_id?: string;
  /** Link to permanent SDR record */
  source_data_record_id?: number;
  /** Error message if failed */
  error_message?: string;
  /** Processing status for the uploaded file (only when include_items=true) */
  processing_status?: SDRProcessingStatus | null;
}

/**
 * Response from polling batch status.
 * GET /datasets/{id}/upload-from-cloud/{batch_id}
 *
 * TODO: Requires backend PR #3163 and #3165 to merge before this type is fully functional.
 */
export interface CloudBatchStatusResponse {
  /** Batch ID */
  batch_id: string;
  /** Current status */
  status: CloudBatchStatus;
  /** Discovery progress */
  discovery: CloudBatchDiscoveryProgress;
  /** Upload progress */
  uploads: CloudBatchUploadProgress;
  /** Individual item statuses (if include_items=true) */
  items?: CloudBatchItemStatus[];
}

/**
 * Summary of a batch in the list response.
 * This is a flattened format returned by GET /datasets/{id}/upload-from-cloud
 */
export interface CloudBatchSummary {
  /** Unique batch ID */
  batch_id: string;
  /** Current status */
  status: CloudBatchStatus;
  /** Number of sources (files + folders) submitted */
  discovery_sources_total: number;
  /** Number of sources with completed discovery */
  discovery_sources_completed: number;
  /** Number of files discovered so far */
  files_discovered: number;
  /** Number of files successfully uploaded */
  files_completed: number;
  /** Number of files that failed to upload */
  files_failed: number;
  /** Timestamp when batch was created */
  created_at: string;
  /** Cloud provider type */
  provider_type: CloudProviderType;
}

/**
 * Response listing active batches for a dataset.
 * GET /datasets/{id}/upload-from-cloud
 *
 * TODO: Requires backend PR #3163 and #3165 to merge before this type is fully functional.
 */
export interface CloudBatchListResponse {
  /** Dataset ID */
  dataset_id: number;
  /** Active batches for this dataset */
  batches: CloudBatchSummary[];
  /** Total number of active batches */
  total_active: number;
}

/**
 * Extended file upload state that supports both local and cloud uploads.
 */
export interface UnifiedUploadFileState {
  /** Unique identifier */
  id: string;
  /** File name */
  name: string;
  /** File size in bytes */
  size: number;
  /** Source type */
  source: "local" | "cloud";
  /** Current status */
  status: "idle" | "pending" | "uploading" | "completed" | "failed";
  /** Upload progress (0-100) */
  progress: number;
  /** Error message if failed */
  error?: string;
  /** For local files: the File object */
  file?: File;
  /** For cloud files: the cloud path */
  cloudPath?: string;
  /** For cloud files: batch ID */
  batchId?: string;
  /** For cloud files: provider type */
  providerType?: CloudProviderType;
  /** Link to SDR after upload starts */
  sourceDataRecordId?: number;
}
